import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import ModalBasic from "@/components/ModalBasic.jsx";
import { PrimaryButton, SecondaryButton } from "@/components/Buttons.jsx";
import GeoJsonPreviewMap, {
  parseGeoJsonGeometry,
  stringifyGeoJsonGeometry,
} from "@/components/GeoJsonPreviewMap.jsx";
import { addToast } from "@/store/app/action.js";
import {
  createProductGeorestriction,
  updateProductGeorestriction,
} from "@/api/productGeorestrictions.js";
import { getPlans as getMarketplaceCategories } from "@/api/marketplace.js";
import { getProductTags } from "@/api/productTags.js";
import ProductAttributeTagSelect from "@/pages/products/ProductAttributeTagSelect.jsx";
import CategoryMultiSelect from "./CategoryMultiSelect.jsx";
import { US_STATES } from "@/utils/usStates.js";

const ADDRESS_OPTIONS = [
  {
    id: "platform",
    label: "Platform wide",
    hint: "Applies everywhere — no address match",
  },
  {
    id: "delivery",
    label: "Delivery address",
    hint: "Customer’s drop-off location",
  },
  {
    id: "store",
    label: "Store address",
    hint: "Where the merchant is located",
  },
  {
    id: "either",
    label: "Store or delivery",
    hint: "Matches if either address is in the area",
  },
];

const MATCH_OPTIONS = [
  { id: "state", label: "Whole state", hint: "e.g. all of California" },
  { id: "state_city", label: "City", hint: "State + city name" },
  { id: "state_county", label: "County", hint: "State + county name" },
  { id: "zip_codes", label: "Zip codes", hint: "One or more ZIP codes" },
  { id: "geojson", label: "Custom area", hint: "Paste a GeoJSON polygon" },
];

const RESTRICT_OPTIONS = [
  { id: "delivery", label: "Block delivery", hint: "Still allow pickup" },
  { id: "pickup", label: "Block pickup", hint: "Still allow delivery" },
  { id: "both", label: "Block both", hint: "No pickup or delivery" },
];

const TARGET_OPTIONS = [
  {
    id: "categories",
    label: "Block categories",
    hint: "Hide every product in the selected categories",
  },
  {
    id: "tags_any",
    label: "Block any tag",
    hint: "Hide products with at least one tag. Categories are optional.",
  },
  {
    id: "tags_all",
    label: "Block all tags",
    hint: "Hide products with every tag. Categories are optional.",
  },
  {
    id: "permissive_tags_any",
    label: "Require any tag",
    hint: "Hide products that have none of the selected tags",
  },
  {
    id: "permissive_tags_all",
    label: "Require all tags",
    hint: "Hide products missing any selected tag",
  },
];

const PERMISSIVE_TARGET_TYPES = new Set([
  "permissive_tags_any",
  "permissive_tags_all",
]);

const ALL_TAGS_TARGET_TYPES = new Set(["tags_all", "permissive_tags_all"]);

// The editor treats "in these categories" as optional on tag rules.
// The API still stores a category-scoped type when categories are chosen.
function uiTargetType(apiType) {
  if (apiType === "categories_tags_any") return "tags_any";
  if (apiType === "categories_tags_all") return "tags_all";
  return apiType || "categories";
}

function savedTargetType(form) {
  const scoped = form.category_ids.length > 0;
  if (form.target_type === "tags_any") {
    return scoped ? "categories_tags_any" : "tags_any";
  }
  if (form.target_type === "tags_all") {
    return scoped ? "categories_tags_all" : "tags_all";
  }
  return form.target_type;
}

function emptyForm() {
  return {
    name: "",
    active: true,
    address_type: "delivery",
    match_type: "state",
    state: "",
    city: "",
    county: "",
    zip_codes_text: "",
    geojson_text: "",
    restrict: "both",
    target_type: "categories",
    category_ids: [],
    except_category_ids: [],
    tags: [],
  };
}

function ruleToForm(rule) {
  if (!rule) return emptyForm();
  const match = rule.match || {};
  return {
    name: rule.name || "",
    active: rule.active !== false,
    address_type: rule.address_type || "delivery",
    match_type: match.type || "state",
    state: match.state || "",
    city: match.city || "",
    county: match.county || "",
    zip_codes_text: Array.isArray(match.zip_codes) ? match.zip_codes.join("\n") : "",
    geojson_text: match.geojson ? JSON.stringify(match.geojson, null, 2) : "",
    restrict: rule.restrict || "both",
    target_type: uiTargetType(rule.target?.type),
    category_ids: Array.isArray(rule.target?.category_ids)
      ? rule.target.category_ids.map((id) => String(id))
      : [],
    except_category_ids: Array.isArray(rule.target?.except_category_ids)
      ? rule.target.except_category_ids.map((id) => String(id))
      : [],
    tags: Array.isArray(rule.target?.tags) ? rule.target.tags : [],
  };
}

function parseZipCodes(text) {
  return String(text || "")
    .split(/[\n,]+/)
    .map((z) => z.trim())
    .filter(Boolean);
}

function stateName(code) {
  return US_STATES.find((s) => s.code === code)?.name || code || "…";
}

function buildMatchPayload(form) {
  if (form.address_type === "platform") {
    return { type: "platform" };
  }
  const match = { type: form.match_type };
  switch (form.match_type) {
    case "state":
      match.state = form.state;
      break;
    case "zip_codes":
      match.zip_codes = parseZipCodes(form.zip_codes_text);
      break;
    case "state_city":
      match.state = form.state;
      match.city = form.city.trim();
      break;
    case "state_county":
      match.state = form.state;
      match.county = form.county.trim();
      break;
    case "geojson": {
      const { geometry, error } = parseGeoJsonGeometry(form.geojson_text);
      if (error || !geometry) {
        throw new Error(error || "GeoJSON must include a Polygon or MultiPolygon");
      }
      match.geojson = geometry;
      break;
    }
    default:
      break;
  }
  return match;
}

function ChoiceCard({ selected, onClick, label, hint, compact = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left rounded-lg border px-3 py-2.5 transition ${
        selected
          ? "border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500 dark:border-indigo-400 dark:bg-indigo-500/10 dark:ring-indigo-400"
          : "border-slate-200 bg-white hover:border-slate-300 dark:border-slate-600 dark:bg-slate-800/40 dark:hover:border-slate-500"
      } ${compact ? "" : "h-full"}`}
    >
      <div className={`text-sm font-medium ${
        selected
          ? "text-indigo-700 dark:text-indigo-300"
          : "text-slate-800 dark:text-slate-100"
      }`}>
        {label}
      </div>
      {hint ? (
        <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400 leading-snug">
          {hint}
        </div>
      ) : null}
    </button>
  );
}

function Section({ step, title, children }) {
  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-800 text-[11px] font-semibold text-white dark:bg-slate-200 dark:text-slate-900">
          {step}
        </span>
        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
          {title}
        </h3>
      </div>
      <div className="pl-0 sm:pl-8 space-y-3">{children}</div>
    </section>
  );
}

function buildRuleSummary(form, categoryOptions, tagOptions) {
  const action =
    form.restrict === "pickup"
      ? "block pickup"
      : form.restrict === "delivery"
        ? "block delivery"
        : "block pickup and delivery";

  const catNames = form.category_ids
    .map((id) => categoryOptions.find((c) => c.id === id)?.name)
    .filter(Boolean);
  const catPart = catNames.length
    ? catNames.length <= 2
      ? catNames.join(" or ")
      : `${catNames.slice(0, 2).join(", ")} +${catNames.length - 2} more`
    : "selected categories";

  const tagNames = form.tags
    .map((id) => {
      const t = tagOptions.find((o) => o.id === id);
      return t?.display?.value || t?.id || id;
    })
    .filter(Boolean);
  const joinTagsWith = ALL_TAGS_TARGET_TYPES.has(form.target_type) ? " and " : " or ";
  const tagPart = tagNames.length
    ? tagNames.length <= 2
      ? tagNames.join(joinTagsWith)
      : `${tagNames.slice(0, 2).join(", ")} +${tagNames.length - 2} more`
    : "selected tags";

  const exceptNames = (form.except_category_ids || [])
    .map((id) => categoryOptions.find((c) => c.id === id)?.name)
    .filter(Boolean);
  const exceptPart = exceptNames.length
    ? exceptNames.length <= 2
      ? exceptNames.join(" or ")
      : `${exceptNames.slice(0, 2).join(", ")} +${exceptNames.length - 2} more`
    : "";

  let productPart = `products in ${catPart}`;
  if (form.target_type === "tags_all" || form.target_type === "tags_any") {
    const scope = form.category_ids.length ? `in ${catPart} ` : "";
    const match = form.target_type === "tags_all" ? "have all of" : "have any of";
    productPart = `products ${scope}that ${match}: ${tagPart}`;
  } else if (PERMISSIVE_TARGET_TYPES.has(form.target_type)) {
    const scope = form.category_ids.length ? `in ${catPart}` : "in any category";
    const except = exceptPart ? `, except ${exceptPart}` : "";
    const missing =
      form.target_type === "permissive_tags_all"
        ? `are missing any of: ${tagPart}`
        : `have none of: ${tagPart}`;
    productPart = `products ${scope}${except} that ${missing}`;
  }

  if (form.address_type === "platform") {
    return `Platform-wide, ${action} for ${productPart}.`;
  }

  const address =
    form.address_type === "store"
      ? "a store’s address"
      : form.address_type === "either"
        ? "the store address or the delivery address"
        : "a delivery address";

  let where = "matches…";
  switch (form.match_type) {
    case "state":
      where = form.state
        ? `is in ${stateName(form.state)}`
        : "is in a selected state";
      break;
    case "state_city":
      where =
        form.state && form.city.trim()
          ? `is in ${form.city.trim()}, ${stateName(form.state)}`
          : "is in a selected city";
      break;
    case "state_county":
      where =
        form.state && form.county.trim()
          ? `is in ${form.county.trim()} County, ${stateName(form.state)}`
          : "is in a selected county";
      break;
    case "zip_codes": {
      const zips = parseZipCodes(form.zip_codes_text);
      where = zips.length
        ? `is in ${zips.length === 1 ? `ZIP ${zips[0]}` : `one of ${zips.length} ZIP codes`}`
        : "is in selected ZIP codes";
      break;
    }
    case "geojson":
      where = form.geojson_text.trim()
        ? "falls inside a custom GeoJSON area"
        : "falls inside a custom area";
      break;
    default:
      break;
  }

  return `When ${address} ${where}, ${action} for ${productPart}.`;
}

function marketplaceCategoryOptions(data) {
  const raw = Array.isArray(data)
    ? data
    : Array.isArray(data?.categories)
      ? data.categories
      : [];
  return raw
    .map((c) => {
      const id = String(c?.id || c?._id || "").trim();
      const name = String(c?.name || c?.label || "").trim();
      if (!id || !name) return null;
      return { id, name, tag: String(c?.tag || "").trim() };
    })
    .filter(Boolean)
    .sort((a, b) => a.name.localeCompare(b.name));
}

function ProductGeorestrictionModal({ isOpen, setIsOpen, rule, onSave }) {
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [tagOptions, setTagOptions] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(false);

  const isEditMode = Boolean(rule?.id);
  const isPlatformWide = form.address_type === "platform";
  const needsState =
    !isPlatformWide && ["state", "state_city", "state_county"].includes(form.match_type);
  const needsCategories = form.target_type === "categories";
  const isPermissive = PERMISSIVE_TARGET_TYPES.has(form.target_type);
  const needsTags = form.target_type !== "categories";
  const zipCount = parseZipCodes(form.zip_codes_text).length;
  const restrictStep = isPlatformWide ? 2 : 3;
  const productsStep = isPlatformWide ? 3 : 4;

  const [debouncedGeojson, setDebouncedGeojson] = useState("");
  useEffect(() => {
    if (form.match_type !== "geojson") return undefined;
    const t = setTimeout(() => setDebouncedGeojson(form.geojson_text), 300);
    return () => clearTimeout(t);
  }, [form.match_type, form.geojson_text]);

  const geojsonPreview = useMemo(
    () => parseGeoJsonGeometry(debouncedGeojson),
    [debouncedGeojson]
  );

  const setField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const summary = useMemo(
    () => buildRuleSummary(form, categoryOptions, tagOptions),
    [form, categoryOptions, tagOptions]
  );

  useEffect(() => {
    if (!isOpen) return;
    setForm(ruleToForm(rule));
  }, [isOpen, rule]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    setCategoriesLoading(true);
    getMarketplaceCategories()
      .then((data) => {
        if (!cancelled) setCategoryOptions(marketplaceCategoryOptions(data));
      })
      .catch(() => {
        if (!cancelled) setCategoryOptions([]);
      })
      .finally(() => {
        if (!cancelled) setCategoriesLoading(false);
      });

    setTagsLoading(true);
    getProductTags()
      .then((tags) => {
        if (!cancelled) setTagOptions(Array.isArray(tags) ? tags : []);
      })
      .catch(() => {
        if (!cancelled) setTagOptions([]);
      })
      .finally(() => {
        if (!cancelled) setTagsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const handleSave = async () => {
    if (needsCategories && !form.category_ids.length) {
      dispatch(addToast({ message: "Select at least one category", type: "error" }));
      return;
    }
    if (needsTags && !form.tags.length) {
      dispatch(addToast({ message: "Select at least one tag", type: "error" }));
      return;
    }
    if (!isPlatformWide) {
      if (needsState && !form.state) {
        dispatch(addToast({ message: "State is required", type: "error" }));
        return;
      }
      if (form.match_type === "state_city" && !form.city.trim()) {
        dispatch(addToast({ message: "City is required", type: "error" }));
        return;
      }
      if (form.match_type === "state_county" && !form.county.trim()) {
        dispatch(addToast({ message: "County is required", type: "error" }));
        return;
      }
      if (form.match_type === "zip_codes" && zipCount === 0) {
        dispatch(addToast({ message: "Enter at least one zip code", type: "error" }));
        return;
      }
    }

    let match;
    try {
      match = buildMatchPayload(form);
    } catch (e) {
      dispatch(addToast({ message: e.message || "Invalid match configuration", type: "error" }));
      return;
    }

    const payload = {
      name: form.name.trim(),
      active: Boolean(form.active),
      address_type: form.address_type,
      match,
      restrict: form.restrict,
      target: {
        type: savedTargetType(form),
        category_ids: needsCategories || form.category_ids.length ? form.category_ids : [],
        except_category_ids: isPermissive ? form.except_category_ids : [],
        tags: needsTags ? form.tags : [],
      },
    };

    try {
      setSaving(true);
      if (isEditMode) {
        await updateProductGeorestriction(rule.id, payload);
        dispatch(addToast({ message: "Georestriction updated", type: "success" }));
      } else {
        await createProductGeorestriction(payload);
        dispatch(addToast({ message: "Georestriction created", type: "success" }));
      }
      onSave?.();
      setIsOpen(false);
    } catch (error) {
      dispatch(addToast({
        message: error.response?.data?.message || error.message || "Failed to save georestriction",
        type: "error",
      }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalBasic
      id="product-georestriction-modal"
      title={isEditMode ? "Edit Product Georestriction" : "Add Product Georestriction"}
      modalOpen={isOpen}
      setModalOpen={setIsOpen}
      width="7xl"
    >
      <div className="px-5 py-4 space-y-6">
        {/* Live plain-English summary */}
        <div className="rounded-lg border border-indigo-200 bg-indigo-50/70 px-4 py-3 dark:border-indigo-500/30 dark:bg-indigo-500/10">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-indigo-600 dark:text-indigo-300 mb-1">
            This rule will
          </div>
          <p className="text-sm text-slate-800 dark:text-slate-100 leading-relaxed">
            {summary}
          </p>
          {!form.active ? (
            <p className="mt-2 text-xs font-medium text-amber-700 dark:text-amber-300">
              Currently inactive — saved but not enforced.
            </p>
          ) : null}
        </div>

        {/* Name + active */}
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-end">
          <div>
            <label className="block text-sm font-medium mb-1">
              Name <span className="text-slate-400 font-normal">(optional)</span>
            </label>
            <input
              className="form-input w-full"
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
              placeholder="e.g. No flavored vapes in NYC"
            />
          </div>
          <label className="flex items-center h-10 px-1">
            <input
              type="checkbox"
              className="form-checkbox"
              checked={form.active}
              onChange={(e) => setField("active", e.target.checked)}
            />
            <span className="text-sm ml-2 whitespace-nowrap">Rule is active</span>
          </label>
        </div>

        <div className="border-t border-slate-200 dark:border-slate-700" />

        <Section step={1} title="Which address should we check?">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {ADDRESS_OPTIONS.map((opt) => (
              <ChoiceCard
                key={opt.id}
                selected={form.address_type === opt.id}
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    address_type: opt.id,
                    match_type:
                      opt.id === "platform"
                        ? "platform"
                        : f.match_type === "platform"
                          ? "state"
                          : f.match_type,
                  }))
                }
                label={opt.label}
                hint={opt.hint}
              />
            ))}
          </div>
        </Section>

        {!isPlatformWide ? (
        <Section step={2} title="How should that address match?">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {MATCH_OPTIONS.map((opt) => (
              <ChoiceCard
                key={opt.id}
                selected={form.match_type === opt.id}
                onClick={() => setField("match_type", opt.id)}
                label={opt.label}
                hint={opt.hint}
              />
            ))}
          </div>

          <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-800/30 p-3 space-y-3">
            {needsState ? (
              <div>
                <label className="block text-sm font-medium mb-1">State</label>
                <select
                  className="form-select w-full"
                  value={form.state}
                  onChange={(e) => setField("state", e.target.value)}
                >
                  <option value="">Select a state</option>
                  {US_STATES.map((s) => (
                    <option key={s.code} value={s.code}>
                      {s.name} ({s.code})
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            {form.match_type === "state_city" ? (
              <div>
                <label className="block text-sm font-medium mb-1">City</label>
                <input
                  className="form-input w-full"
                  value={form.city}
                  onChange={(e) => setField("city", e.target.value)}
                  placeholder="e.g. New York"
                />
              </div>
            ) : null}

            {form.match_type === "state_county" ? (
              <div>
                <label className="block text-sm font-medium mb-1">County</label>
                <input
                  className="form-input w-full"
                  value={form.county}
                  onChange={(e) => setField("county", e.target.value)}
                  placeholder="e.g. Los Angeles"
                />
              </div>
            ) : null}

            {form.match_type === "zip_codes" ? (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium">Zip codes</label>
                  {zipCount > 0 ? (
                    <span className="text-xs text-slate-500">{zipCount} entered</span>
                  ) : null}
                </div>
                <textarea
                  className="form-textarea w-full min-h-[100px] font-mono text-sm"
                  value={form.zip_codes_text}
                  onChange={(e) => setField("zip_codes_text", e.target.value)}
                  placeholder={"10001\n10002\n10003"}
                />
                <p className="text-xs text-slate-500 mt-1">
                  One per line, or comma-separated.
                </p>
              </div>
            ) : null}

            {form.match_type === "geojson" ? (
              <div className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <label className="block text-sm font-medium">GeoJSON area</label>
                  <span
                    className={`text-xs font-medium ${
                      !String(form.geojson_text || "").trim()
                        ? "text-slate-400"
                        : geojsonPreview.error
                          ? "text-rose-500"
                          : "text-emerald-600 dark:text-emerald-400"
                    }`}
                  >
                    {!String(form.geojson_text || "").trim()
                      ? "Draw on the map or paste GeoJSON"
                      : geojsonPreview.error
                        ? geojsonPreview.error
                        : `Valid ${geojsonPreview.geometry?.type}`}
                  </span>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  <div className="min-w-0 flex flex-col">
                    <textarea
                      className="form-textarea w-full flex-1 min-h-[280px] lg:min-h-[320px] font-mono text-xs sm:text-sm resize-y"
                      value={form.geojson_text}
                      onChange={(e) => setField("geojson_text", e.target.value)}
                      placeholder={`{\n  "type": "Polygon",\n  "coordinates": [[[lng, lat], ...]]\n}`}
                      spellCheck={false}
                    />
                    <p className="text-xs text-slate-500 mt-1.5">
                      Draw on the map, or paste a Polygon/MultiPolygon, Feature, or FeatureCollection.
                    </p>
                  </div>
                  <div className="min-w-0">
                    <GeoJsonPreviewMap
                      geometry={geojsonPreview.geometry}
                      height={320}
                      editable
                      onGeometryChange={(next) => {
                        setField(
                          "geojson_text",
                          next ? stringifyGeoJsonGeometry(next) : ""
                        );
                      }}
                    />
                  </div>
                </div>
              </div>
            ) : null}

            {form.match_type === "state" && form.state ? (
              <p className="text-xs text-slate-500">
                Matches any{" "}
                {form.address_type === "store"
                  ? "store"
                  : form.address_type === "either"
                    ? "store or delivery"
                    : "delivery"}{" "}
                address in{" "}
                <span className="font-medium text-slate-700 dark:text-slate-200">
                  {stateName(form.state)}
                </span>
                .
              </p>
            ) : null}
          </div>
        </Section>
        ) : null}

        <Section step={restrictStep} title="What should we block?">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {RESTRICT_OPTIONS.map((opt) => (
              <ChoiceCard
                key={opt.id}
                selected={form.restrict === opt.id}
                onClick={() => setField("restrict", opt.id)}
                label={opt.label}
                hint={opt.hint}
              />
            ))}
          </div>
        </Section>

        <Section step={productsStep} title="Which products does this apply to?">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {TARGET_OPTIONS.map((opt) => (
              <ChoiceCard
                key={opt.id}
                selected={form.target_type === opt.id}
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    target_type: opt.id,
                    except_category_ids: PERMISSIVE_TARGET_TYPES.has(opt.id)
                      ? f.except_category_ids
                      : [],
                    tags: opt.id === "categories" ? [] : f.tags,
                  }))
                }
                label={opt.label}
                hint={opt.hint}
              />
            ))}
          </div>

          {needsCategories ? (
            <div>
              <label className="block text-sm font-medium mb-1">
                Categories <span className="text-rose-500">*</span>
              </label>
              <CategoryMultiSelect
                options={categoryOptions}
                value={form.category_ids}
                onChange={(ids) => setField("category_ids", ids)}
                loading={categoriesLoading}
                emptyPlaceholder="Select categories"
              />
            </div>
          ) : null}

          {!needsCategories ? (
            <div>
              <label className="block text-sm font-medium mb-1">
                Categories{" "}
                <span className="text-slate-400 font-normal">(optional)</span>
              </label>
              <CategoryMultiSelect
                options={categoryOptions}
                value={form.category_ids}
                onChange={(ids) => setField("category_ids", ids)}
                loading={categoriesLoading}
                emptyPlaceholder="All categories"
              />
              <p className="text-xs text-slate-500 mt-1">
                Leave empty to apply this rule to every category.
              </p>
            </div>
          ) : null}

          {isPermissive ? (
            <div>
                <label className="block text-sm font-medium mb-1">
                  Except categories{" "}
                  <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <CategoryMultiSelect
                  options={categoryOptions}
                  value={form.except_category_ids}
                  onChange={(ids) => setField("except_category_ids", ids)}
                  loading={categoriesLoading}
                  emptyPlaceholder="No exceptions"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Products in these categories stay available even when they are missing the tags.
                </p>
            </div>
          ) : null}

          {needsTags ? (
            <div>
              <label className="block text-sm font-medium mb-1">
                Attribute tags <span className="text-rose-500">*</span>
              </label>
              <ProductAttributeTagSelect
                options={tagOptions}
                value={form.tags}
                onChange={(ids) => setField("tags", ids)}
                loading={tagsLoading}
              />
              <p className="text-xs text-slate-500 mt-1">
                {form.target_type === "permissive_tags_all"
                  ? "A product is hidden unless it has every selected tag."
                  : form.target_type === "permissive_tags_any"
                    ? "A product is hidden unless it has at least one selected tag."
                    : ALL_TAGS_TARGET_TYPES.has(form.target_type)
                      ? "A product is hidden when it has every selected tag."
                      : "A product is hidden when it has at least one of the selected tags."}
              </p>
            </div>
          ) : null}
        </Section>

        <div className="flex flex-wrap justify-end gap-2 border-t border-slate-200 dark:border-slate-700 pt-4">
          <SecondaryButton type="button" onClick={() => setIsOpen(false)} disabled={saving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton type="button" onClick={handleSave} loading={saving} disabled={saving}>
            {saving ? "Saving…" : isEditMode ? "Save changes" : "Create rule"}
          </PrimaryButton>
        </div>
      </div>
    </ModalBasic>
  );
}

export default ProductGeorestrictionModal;
